import { useMemo, useState, type MouseEvent as ReactMouseEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueries } from '@tanstack/react-query';
import {
  Card,
  EpochBar,
  HeroCard,
  SectionHeader,
} from '@gonka-wallet/ui-tokens';
import { WalletGlyph } from '../components/WalletGlyph.js';
import { useMinerRewards } from '../hooks/useMinerRewards.js';
import { formatGnkDisplay } from '../lib/format.js';
import { useWalletStore } from '../stores/walletStore.js';
import { useWatchedModalStore } from '../stores/watchedModalStore.js';
import { useEpoch } from '../hooks/useEpoch.js';
import { useVestingAggregate } from '../hooks/useVestingAggregate.js';
import {
  MinerDetailModal,
  type MinerDetailTab,
} from '../components/miner/MinerDetailModal.js';
import { MinerIcon } from '../components/icons.js';
import {
  fetchMinerStats,
  type MinerStats,
  type MinerStatsResponse,
} from '../lib/minerApi.js';

const GPU_WEIGHT_FACTOR = 254.5;

function formatSecondsRemaining(seconds: number | undefined): string | null {
  if (seconds === undefined || seconds < 0) return null;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function formatUTCTime(iso: string | undefined): string | null {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    const hh = String(d.getUTCHours()).padStart(2, '0');
    const mm = String(d.getUTCMinutes()).padStart(2, '0');
    return `${hh}:${mm} UTC`;
  } catch {
    return null;
  }
}

function formatShort(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toFixed(0);
}

type WalletEntry = {
  readonly kind: 'full' | 'watch';
  readonly id: string;
  readonly name: string;
  readonly address: string;
};

export function Miner(): ReactNode {
  const { t } = useTranslation();
  const wallets = useWalletStore((s) => s.wallets);
  const watched = useWalletStore((s) => s.watched);
  const openWatchedModal = useWatchedModalStore((s) => s.open);

  const entries: WalletEntry[] = useMemo(
    () => [
      ...wallets.map((w) => ({
        kind: 'full' as const,
        id: w.id,
        name: w.name,
        address: w.addresses[0] ?? '',
      })),
      ...watched.map((w) => ({
        kind: 'watch' as const,
        id: w.id,
        name: w.name,
        address: w.address,
      })),
    ],
    [wallets, watched],
  );

  // Parallel fetch miner-stats for every wallet address. Reusing the same
  // query key as useMinerStats so the cache is shared with MinerDetailModal.
  const statsQueries = useQueries({
    queries: entries.map((e) => ({
      queryKey: ['miner-stats', e.address],
      queryFn: async (): Promise<MinerStatsResponse | null> => {
        if (!e.address) return null;
        return fetchMinerStats(e.address);
      },
      enabled: !!e.address,
      staleTime: 60_000,
      retry: 1,
    })),
  });

  const statsByAddress = useMemo(() => {
    const map = new Map<string, MinerStats | null>();
    entries.forEach((e, i) => {
      const q = statsQueries[i];
      map.set(e.address, q?.data?.miner ?? null);
    });
    return map;
  }, [entries, statsQueries]);

  // Aggregates across all wallets. Totals rolled up even for inactive ones
  // so the HeroCard always reflects the full portfolio picture.
  const aggregate = useMemo(() => {
    let weight = 0;
    let activeCount = 0;
    let bestRank: number | null = null;
    let totalParticipants = 0;
    let shareSum = 0;
    for (const m of statsByAddress.values()) {
      if (!m) continue;
      weight += m.weight ?? 0;
      if (m.is_active) activeCount += 1;
      if (m.rank !== null && (bestRank === null || m.rank < bestRank)) {
        bestRank = m.rank;
      }
      totalParticipants = m.total_participants || totalParticipants;
      shareSum += m.share ?? 0;
    }
    const h100 = Math.round(weight / GPU_WEIGHT_FACTOR);
    return {
      weight,
      h100,
      activeCount,
      rank: bestRank,
      totalParticipants,
      share: shareSum,
    };
  }, [statsByAddress]);

  const epochQuery = useEpoch();
  const epochId = epochQuery.data?.epoch_id ?? null;
  const epochProgress = epochQuery.data?.epoch_timing?.epoch_progress ?? 0;
  const epochRemaining = formatSecondsRemaining(
    epochQuery.data?.epoch_timing?.seconds_remaining ??
      (epochQuery.data?.epoch_timing?.hours_remaining !== undefined
        ? Math.round(epochQuery.data.epoch_timing.hours_remaining * 3600)
        : undefined),
  );

  // Only actively mining wallets are shown in the Miner tab.
  const activeRows = useMemo(
    () =>
      entries.filter((e) => {
        const m = statsByAddress.get(e.address);
        return m?.is_active === true;
      }),
    [entries, statsByAddress],
  );

  const [drillInAddress, setDrillInAddress] = useState<string | null>(null);
  const [drillInTab, setDrillInTab] = useState<MinerDetailTab>('details');
  const drillInStats = drillInAddress
    ? (statsByAddress.get(drillInAddress) ?? null)
    : null;

  function openDrill(address: string, tab: MinerDetailTab = 'details'): void {
    setDrillInAddress(address);
    setDrillInTab(tab);
  }

  // Vesting aggregated only across actively mining wallets so the Miner
  // HeroCard reflects the mining balance specifically.
  const addressesForVesting = useMemo(
    () => activeRows.map((e) => e.address).filter(Boolean),
    [activeRows],
  );
  const vesting = useVestingAggregate(addressesForVesting);

  const showEmpty = entries.length === 0;

  return (
    <div className="flex min-h-screen flex-col gap-4 p-4">
      {showEmpty ? (
        <Card className="flex flex-col items-center gap-3 p-8 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-surface-2 text-text-secondary">
            <MinerIcon className="h-6 w-6" />
          </div>
          <div className="text-base font-semibold text-text-primary">
            {t('miner.emptyTitle')}
          </div>
          <div className="max-w-xs text-sm text-text-secondary">
            {t('miner.emptyBody')}
          </div>
          <button
            type="button"
            onClick={openWatchedModal}
            className="mt-2 rounded-xl bg-link px-4 py-2 text-sm font-semibold text-bg"
          >
            + {t('watchedAddress.add')}
          </button>
        </Card>
      ) : (
        <>
          <HeroCard
            title={t('miner.totalMiningBalance')}
            balanceNgonka={vesting.total}
            spendableNgonka={vesting.unlocked}
            vestingNgonka={vesting.locked}
            formatNgonka={formatGnkDisplay}
            collapsible
            stats={{
              weight: formatShort(aggregate.weight),
              gpu: '~' + aggregate.h100,
              share: aggregate.share.toFixed(2) + '%',
            }}
            meta={[
              { value: formatShort(aggregate.weight) + ' wt' },
              { value: '~' + aggregate.h100 + ' H100' },
              { value: aggregate.share.toFixed(2) + '%' },
            ]}
            labels={{
              vestingUnlocked: t('wallet.vestingUnlocked'),
              vestingInProgress: t('wallet.vestingInProgress'),
            }}
          />

          {epochId !== null && (
            <EpochBar
              epochLabel={t('miner.epochLabel', { id: epochId })}
              phase={epochQuery.data?.epoch_timing?.epoch_phase ?? 'inference'}
              progress={epochProgress}
              remaining={epochRemaining}
              utcEnd={formatUTCTime(epochQuery.data?.epoch_timing?.estimated_end)}
            />
          )}

          {activeRows.length > 0 && (
            <section className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <SectionHeader>
                  {t('miner.activeWallets')} · {activeRows.length}
                </SectionHeader>
                <button
                  type="button"
                  onClick={openWatchedModal}
                  className="text-xs font-semibold text-link"
                >
                  + {t('wallet.add')}
                </button>
              </div>
              <div className="flex flex-col gap-2">
                {activeRows.map((e) => {
                  const miner = statsByAddress.get(e.address) ?? null;
                  return (
                    <MinerRow
                      key={`${e.kind}-${e.id}`}
                      name={e.name}
                      address={e.address}
                      miner={miner}
                      isWatch={e.kind === 'watch'}
                      onClick={() => openDrill(e.address, 'details')}
                    />
                  );
                })}
              </div>
            </section>
          )}
        </>
      )}

      {drillInAddress && (
        <MinerDetailModal
          address={drillInAddress}
          minerStats={drillInStats}
          currentEpoch={epochId}
          initialTab={drillInTab}
          onClose={() => setDrillInAddress(null)}
        />
      )}
    </div>
  );
}

function MinerRow({
  name,
  address,
  miner,
  isWatch,
  onClick,
}: {
  name: string;
  address: string;
  miner: MinerStats | null;
  isWatch: boolean;
  onClick: () => void;
}): ReactNode {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const isActive = !!miner?.is_active;
  const rank = miner?.rank ?? null;
  const weight = miner?.weight ?? 0;
  const missed = miner?.missed_percent ?? 0;
  const isHealthy = miner?.is_healthy;
  const isJailed = miner?.is_jailed;
  const hasCollateral = miner?.has_collateral;
  const collateralOk = miner?.collateral_is_sufficient;
  const rewardsQuery = useMinerRewards(isActive ? address : null, 1);
  const lastReward = rewardsQuery.data?.rewards?.[0];
  const claimStatus = lastReward ? lastReward.claimed : null;
  const claimLoading =
    isActive &&
    (rewardsQuery.isLoading || (rewardsQuery.isFetching && !rewardsQuery.data));

  const dotColor = isActive
    ? 'var(--color-pos)'
    : isWatch
      ? 'var(--color-text-tertiary)'
      : null;
  const MAX_NAME = 10;
  const displayName = name.length > MAX_NAME ? `${name.slice(0, MAX_NAME)}…` : name;
  const shortAddr = `${address.slice(0, 9)}…${address.slice(-4)}`;

  const onCopy = (event: ReactMouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    try {
      void navigator.clipboard?.writeText(address);
    } catch {
      /* ignore */
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1400);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick();
        }
      }}
      className="grid w-full cursor-pointer grid-cols-[36px_minmax(0,1fr)_auto] items-center gap-x-3 rounded-2xl border border-border-hairline bg-surface-1 px-4 py-3 text-left outline-none transition-colors hover:bg-surface-2 focus-visible:ring-2 focus-visible:ring-[color:var(--color-link)]"
    >
      <WalletGlyph seed={address} size={36} />

      <div className="flex min-w-0 flex-col">
        <div className="flex min-w-0 items-center gap-2">
          <span className="flex h-2 w-2 shrink-0 items-center justify-center">
            {dotColor && (
              <span
                aria-label={isActive ? t('miner.statusActive') : t('watchedAddress.watchBadge')}
                className="inline-block h-2 w-2 rounded-full"
                style={{ background: dotColor }}
              />
            )}
          </span>
          <span className="inline-block w-[6.25rem] overflow-hidden text-ellipsis whitespace-nowrap text-[14px] font-semibold leading-[1.1] tracking-[-0.2px] text-text-primary">
            {displayName}
          </span>
        </div>
        <div className="mt-[5px] flex min-w-0 items-center gap-1.5">
          <span className="overflow-hidden text-ellipsis whitespace-nowrap font-mono text-[11px] leading-[1.1] tracking-[0.2px] text-text-tertiary tabular-nums">
            {shortAddr}
          </span>
          <button
            type="button"
            onClick={onCopy}
            aria-label={copied ? 'Copied' : 'Copy address'}
            className={[
              'inline-grid h-[18px] w-[18px] shrink-0 place-items-center rounded border-0 bg-transparent p-0 transition-colors',
              copied ? 'text-text-primary' : 'text-text-tertiary hover:text-text-secondary',
            ].join(' ')}
          >
            {copied ? (
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
                <path
                  d="M2.5 6.5 L5 9 L9.5 3.5"
                  stroke="currentColor"
                  strokeWidth={1.5}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            ) : (
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
                <rect x="3.5" y="3.5" width="6" height="6" rx="1.2" stroke="currentColor" strokeWidth={1.2} />
                <path
                  d="M2 7.5 V2.8 A0.8 0.8 0 0 1 2.8 2 H7.5"
                  stroke="currentColor"
                  strokeWidth={1.2}
                  strokeLinecap="round"
                />
              </svg>
            )}
          </button>
        </div>
      </div>

      <div className="flex flex-col items-end gap-0.5">
        <div className="flex items-center gap-1.5 font-mono text-[11px] tabular-nums">
          {rank !== null && (
            <span className="text-text-primary">#{rank}</span>
          )}
          <span className="text-text-tertiary">·</span>
          <span className="text-text-primary">{formatShort(weight)}</span>
        </div>
        <div className="flex items-center gap-1.5 text-[10px]">
          <span
            className={`font-mono tabular-nums ${missed > 5 ? 'text-warning' : missed > 0 ? 'text-text-secondary' : 'text-text-tertiary'}`}
          >
            {missed.toFixed(1)}% miss
          </span>
          <span aria-hidden className="text-text-disabled">·</span>
          <SignalCluster
            health={
              isHealthy === true ? 'pos' : isHealthy === false ? 'neg' : 'muted'
            }
            healthTitle={
              isHealthy === true
                ? t('minerDetail.healthy')
                : isHealthy === false
                  ? t('minerDetail.unhealthy')
                  : 'Health · —'
            }
            jail={
              isJailed === true
                ? 'neg'
                : isJailed === false
                  ? 'pos'
                  : 'muted'
            }
            jailTitle={
              isJailed === true
                ? t('minerDetail.jail')
                : isJailed === false
                  ? t('minerDetail.jailFree')
                  : 'Jail · —'
            }
            collateral={
              hasCollateral === false || hasCollateral === null
                ? 'muted'
                : collateralOk
                  ? 'pos'
                  : 'neg'
            }
            collateralTitle={
              hasCollateral === false || hasCollateral === null
                ? 'Collateral · —'
                : collateralOk
                  ? t('minerDetail.collateralOk')
                  : t('minerDetail.losingRewards')
            }
            claim={
              claimLoading
                ? 'loading'
                : claimStatus === true
                  ? 'pos'
                  : claimStatus === false
                    ? 'warning'
                    : 'muted'
            }
            claimTitle={
              claimLoading
                ? 'Claim · …'
                : claimStatus === true
                  ? 'Claim · claimed'
                  : claimStatus === false
                    ? 'Claim · pending'
                    : 'Claim · —'
            }
          />
        </div>
      </div>
    </div>
  );
}

function SignalCluster({
  health,
  healthTitle,
  jail,
  jailTitle,
  collateral,
  collateralTitle,
  claim,
  claimTitle,
}: {
  health: 'pos' | 'neg' | 'muted';
  healthTitle: string;
  jail: 'pos' | 'neg' | 'muted';
  jailTitle: string;
  collateral: 'pos' | 'neg' | 'muted';
  collateralTitle: string;
  claim: 'pos' | 'warning' | 'muted' | 'loading';
  claimTitle: string;
}): ReactNode {
  return (
    <span className="inline-flex items-center gap-1">
      <Dot tone={health} title={`Health · ${healthTitle}`} />
      <Dot tone={jail} title={`Jail · ${jailTitle}`} />
      <Dot tone={collateral} title={`Collateral · ${collateralTitle}`} />
      <Dot tone={claim} title={claimTitle} />
    </span>
  );
}

function Dot({
  tone,
  title,
}: {
  tone: 'pos' | 'neg' | 'warning' | 'muted' | 'loading';
  title: string;
}): ReactNode {
  const color =
    tone === 'pos'
      ? 'var(--color-pos)'
      : tone === 'neg'
        ? 'var(--color-neg)'
        : tone === 'warning'
          ? 'var(--color-warning)'
          : 'var(--color-text-disabled)';
  return (
    <span
      aria-label={title}
      title={title}
      className={[
        'inline-block h-1.5 w-1.5 rounded-full',
        tone === 'loading' && 'animate-pulse',
      ]
        .filter(Boolean)
        .join(' ')}
      style={{ background: color }}
    />
  );
}

